import { buildApiUrl } from '@/api/baseUrl'
import {
  request,
  getAccessToken,
  refreshAccessToken,
  notifyAuthExpired,
  ApiError,
} from '@/api/client'
import type { UserInfo } from '@/api/auth'  // reuse the same interface

export const getMe = () =>
  request<UserInfo>('GET', '/app/user/me')

export interface PointsLog {
  id: string
  delta: number
  balance_after: number
  reason: string
  created_at: string
}
export interface PointsLogsResponse {
  items: PointsLog[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

export const getPointsBalance = () =>
  request<{ balance: number }>('GET', '/app/points/balance')

export const getPointsLogs = (page = 1, pageSize = 20) =>
  request<PointsLogsResponse>('GET', `/app/points/logs?page=${page}&page_size=${pageSize}`)

export const uploadAvatar = (file: File): Promise<{ url: string }> => {
  const executeUpload = (accessToken: string | null) => {
    const form = new FormData()
    form.append('file', file)
    return fetch(buildApiUrl('/app/user/upload-avatar'), {
      method: 'POST',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      body: form,
    })
  }
  return executeUpload(getAccessToken()).then(async (res) => {
    if (res.status !== 401) return res
    const newToken = await refreshAccessToken()
    if (!newToken) {
      notifyAuthExpired()
      throw new ApiError('AUTH_001', '登录已过期，请重新登录', 401)
    }
    const retry = await executeUpload(newToken)
    if (retry.status === 401) {
      notifyAuthExpired()
      throw new ApiError('AUTH_001', '登录已过期，请重新登录', 401)
    }
    return retry
  }).then((r) => r.json()).then((r) => {
    if (!r.success) throw new ApiError(r.error.code, r.error.message, 400)
    return r.data as { url: string }
  })
}

export const deleteMe = () =>
  request<null>('DELETE', '/app/user/me')

export type LoginProvider = 'phone_otp' | 'email_otp' | 'google' | 'apple'

export interface AccountSecurity {
  login_account: {
    provider: LoginProvider
    masked_identifier: string | null
  }
  contacts: Array<{
    type: 'phone' | 'email'
    masked_value: string
    verified: boolean
    login_enabled: false
  }>
}

export interface ContactChallenge {
  challenge_id: string
  expire_seconds: number
  rate_seconds: number
}

export const getAccountSecurity = () =>
  request<AccountSecurity>('GET', '/app/user/account-security')

export const sendPhoneContactCode = (phone: string) =>
  request<ContactChallenge>('POST', '/app/user/contacts/phone/send-code', {
    body: { phone },
  })

export const bindPhoneContact = (phone: string, challengeId: string, otp: string) =>
  request<AccountSecurity>('PUT', '/app/user/contacts/phone', {
    body: { phone, challenge_id: challengeId, otp },
  })

export const removePhoneContact = () =>
  request<null>('DELETE', '/app/user/contacts/phone')

export const sendEmailContactCode = (email: string) =>
  request<ContactChallenge>('POST', '/app/user/contacts/email/send-code', {
    body: { email },
  })

export const bindEmailContact = (email: string, challengeId: string, otp: string) =>
  request<AccountSecurity>('PUT', '/app/user/contacts/email', {
    body: { email, challenge_id: challengeId, otp },
  })

export const removeEmailContact = () =>
  request<null>('DELETE', '/app/user/contacts/email')

export const updateMe = (data: { nickname?: string; avatar?: string }) =>
  request<UserInfo>('PATCH', '/app/user/me', {
    body: data,
  })
